import React, { useEffect, useState } from 'react';
import api from '../api';

// The sessions a coach can pick from. Seeded once from the club's own team
// list, then maintained here — the club adds and renames teams every season.
const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: '', team_group: 'Seniors' });
  const [editing, setEditing] = useState(null); // { id, name }
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = () => api.get('/api/teams?all=true').then(res => setTeams(res.data.teams));
  useEffect(() => { load(); }, []);

  // Offer the groups already in use, so a new team lands in the right place
  // without anyone having to remember the exact spelling.
  const groups = Array.from(new Set(teams.map(t => t.team_group).filter(Boolean)));

  const add = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    try {
      await api.post('/api/teams', form);
      setNotice(`${form.name} added.`);
      setForm({ name: '', team_group: form.team_group });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add the team');
    }
  };

  const save = async (id) => {
    setError(''); setNotice('');
    try {
      await api.patch(`/api/teams/${id}`, { name: editing.name });
      setNotice('Team renamed. Sessions already logged keep the old name.');
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not rename the team');
    }
  };

  const toggle = async (team) => {
    setError(''); setNotice('');
    try {
      await api.patch(`/api/teams/${team.id}`, { active: !team.active });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update the team');
    }
  };

  return (
    <>
      <h1>Sessions</h1>
      <div className="card">
        <h2>Add a session</h2>
        <p className="muted">
          These are the sessions a coach picks from when logging hours. Retiring one takes it out of the
          coach's list but leaves everything already logged against it alone.
        </p>
        {error && <div className="error-msg">{error}</div>}
        {notice && <div className="success-msg">{notice}</div>}
        <form onSubmit={add}>
          <div className="form-grid">
            <div className="form-row">
              <label>Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-row">
              <label>Group</label>
              <input value={form.team_group} list="team-groups"
                     onChange={e => setForm({ ...form, team_group: e.target.value })} />
              <datalist id="team-groups">
                {groups.map(g => <option key={g} value={g} />)}
              </datalist>
            </div>
          </div>
          <button className="btn" type="submit">Add session</button>
        </form>
      </div>

      <div className="card">
        <h2>All sessions ({teams.filter(t => t.active).length} active)</h2>
        {teams.length === 0 ? <p className="muted">Nothing set up yet.</p> : (
          <table>
            <thead><tr><th>Name</th><th>Group</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {teams.map(t => (
                <tr key={t.id}>
                  <td>
                    {editing && editing.id === t.id
                      ? <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
                      : t.name}
                  </td>
                  <td>{t.team_group || '—'}</td>
                  <td><span className={`badge ${t.active ? 'approved' : 'draft'}`}>{t.active ? 'Active' : 'Retired'}</span></td>
                  <td>
                    {editing && editing.id === t.id ? (
                      <>
                        <button className="btn small" onClick={() => save(t.id)}>Save</button>{' '}
                        <button className="btn small secondary" onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn small secondary" onClick={() => setEditing({ id: t.id, name: t.name })}>Rename</button>{' '}
                        <button className="btn small secondary" onClick={() => toggle(t)}>{t.active ? 'Retire' : 'Bring back'}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default Teams;
